import {
  IDepartment,
  ICurrency,
  ICurrencyData,
  IGLAccount,
  IUser,
  IUserData,
  ICountry,
  IDepartmentData,
  IGLAccountData,
  IFieldErrorsData,
  IFieldErrors,
  IMentionCommentData,
  IMentionComment
} from '@/models';

import {
  IRequisitionAttachmentData,
  IRequisitionComment,
  IRequisitionCommentData,
  IRequisitionLineData,
  IRequisitionLineFieldErrorData,
  IRequisitionStatusData,
  IRequisitionSupplierAddressData,
  IRequisitionSupplierAddressFieldErrorData,
  IRequisitionSupplierContactData,
  IRequisitionSupplierContactFieldErrorData
} from '../requisition';

// API Data

export type IPaymentRequisitionSupplierAddressData = IRequisitionSupplierAddressData;

export type IPaymentRequisitionSupplierContactData = IRequisitionSupplierContactData;

export type IPaymentRequisitionStatusData = IRequisitionStatusData;

export type IPaymentRequisitionAttachmentData = IRequisitionAttachmentData;

export type IPaymentRequisitionCommentData = IRequisitionCommentData;

export interface IPaymentRequisitionLineData {
  id?: number;
  item: string;
  description: string;
  total_price?: number;
  account?: IGLAccountData | number | null;
  department?: IDepartmentData | number | null;
}

export interface IPaymentRequisitionSupplierData {
  id?: number;
  name?: string;
  vendor_code?: string;
  address?: IPaymentRequisitionSupplierAddressData;
  contact?: IPaymentRequisitionSupplierContactData;
}

export interface IPaymentRequisitionApprovalData {
  id?: number;
  status: PaymentRequisitionApprovalState;
  approver: IUserData;
  created_at?: Date | string;
  updated_at?: Date | string;
}

export interface IPaymentRequisitionData {
  id?: number;
  amount?: number;
  num_items?: number;
  payment?: PaymentRequisitionPaymentType;
  po_number?: string;
  note?: string;
  reason?: string;
  status?: IPaymentRequisitionStatusData;
  currency?: ICurrencyData | number;
  attachments?: IPaymentRequisitionAttachmentData[];
  lines?: IPaymentRequisitionLineData[];
  supplier?: IPaymentRequisitionSupplierData;
  is_urgent?: boolean;
  is_provide_payment_detail?: boolean;
  required_date?: Date | string;
  created_at?: Date | string;
  updated_at?: Date | string;

  approvers?: IPaymentRequisitionApprovalData[];
  ap_specialist?: IUserData | number;
  owner?: IUserData | number;
}

export type IPaymentRequisitionSupplierAddressFieldErrorData = IRequisitionSupplierAddressFieldErrorData;

export type IPaymentRequisitionSupplierContactFieldErrorData = IRequisitionSupplierContactFieldErrorData;

export type IPaymentRequisitionLineFieldErrorData = IRequisitionLineFieldErrorData;

export interface IPaymentRequisitionSupplierFieldErrorData {
  name?: string[];
  vendor_code?: string[];
  address?: IPaymentRequisitionSupplierAddressFieldErrorData;
  contact?: IPaymentRequisitionSupplierContactFieldErrorData;
}

export interface IPaymentRequisitionFieldErrorData {
  currency?: string[];
  reason?: string[];
  required_date?: string[];
  po_number?: string[];
  is_urgent?: string[];
  note?: string[];
  attachments?: IFieldErrorsData[];
  payment?: string[];
  supplier?: IPaymentRequisitionSupplierFieldErrorData;
  lines?: IPaymentRequisitionLineFieldErrorData[];
}

// Business Logic Interface
export interface IPaymentRequisitionSupplierAddress
  extends IPaymentRequisitionSupplierAddressData {
  country?: ICountry | string;

  validate(): boolean;
}

export interface IPaymentRequisitionSupplierContact
  extends IPaymentRequisitionSupplierContactData {
  validate(): boolean;
}

export interface IPaymentRequisitionSupplier
  extends IPaymentRequisitionSupplierData {
  address?: IPaymentRequisitionSupplierAddress;
  contact?: IPaymentRequisitionSupplierContact;

  validate(): boolean;
}

export interface IPaymentRequisitionLine extends IPaymentRequisitionLineData {
  account: IGLAccount | number | null;
  department: IDepartment | number | null;
  validate(): boolean;
}

export interface IPaymentRequisitionStatus
  extends IPaymentRequisitionStatusData {
  getColor(): string;
  getIcon(): string;
}

export interface IPaymentRequisitionAttachment
  extends IPaymentRequisitionAttachmentData {
  validate(): boolean;
}

export interface IPaymentRequisitionApproval
  extends IPaymentRequisitionApprovalData {
  approver: IUser;
  validate(): boolean;
}

export type IPaymentRequisitionComment = IRequisitionComment;

export interface IPaymentRequisition extends IPaymentRequisitionData {
  status: IPaymentRequisitionStatus;
  payment?: PaymentRequisitionPaymentType;
  currency?: ICurrency | number;
  account?: IGLAccount | number;
  department?: IDepartment | number;
  attachments: IPaymentRequisitionAttachment[];
  lines: IPaymentRequisitionLine[];
  supplier?: IPaymentRequisitionSupplier;
  required_date?: Date | string;

  note?: string;
  is_provide_payment_detail?: boolean;

  approvers?: IPaymentRequisitionApproval[];
  comments?: IPaymentRequisitionComment[];
  ap_specialist?: IUser | number;
  owner?: IUser | number;

  validate(): boolean;

  // Manage requisition lines
  addLine(line: IPaymentRequisitionLine): void;
  removeLine(index: number): void;

  // Manage attachments
  attachFile(file: File): void;
  detachFile(file: File): void;
}

export interface IPaymentRequisitionSupplierAddressFieldError
  extends IPaymentRequisitionSupplierAddressFieldErrorData {
  clearErrors(): void;
}

export interface IPaymentRequisitionSupplierContactFieldError
  extends IPaymentRequisitionSupplierContactFieldErrorData {
  clearErrors(): void;
}

export interface IPaymentRequisitionSupplierFieldError
  extends IPaymentRequisitionSupplierFieldErrorData {
  address?: IPaymentRequisitionSupplierAddressFieldError;
  contact?: IPaymentRequisitionSupplierContactFieldError;

  clearErrors(): void;
}

export interface IPaymentRequisitionLineFieldError
  extends IPaymentRequisitionLineFieldErrorData {
  clearErrors(): void;
}

export interface IPaymentRequisitionFieldError
  extends IPaymentRequisitionFieldErrorData {
  supplier?: IPaymentRequisitionSupplierFieldError;
  lines: IPaymentRequisitionLineFieldError[];
  attachments: IFieldErrors[];

  clearErrors(): void;
}

// Constants - Used for validation
export const PAYMENT_REQUISITION_LINE_ITEM_MAX_DESCRIPTION_LENGTH = 255;

export enum PaymentRequisitionStatusEnum {
  Draft = 0,
  Submitted,
  InReview,
  Approval2Pending,
  Approved,
  APSpecialistAssigned,
  Rejected,
  Cancelled,
  Paid
}

export enum PaymentRequisitionApprovalState {
  TENTATIVE = 0,
  APPROVED,
  REJECTED
}

export enum PaymentRequisitionPaymentType {
  CASH = 0,
  CARD,
  CHEQUE,
  WIRE
}

export interface PaymentRequisitionFilterParams {
  state?: PaymentRequisitionStatusEnum;
  owner?: number;
  po_number?: string;
}
